import {useEffect, useState} from "react";
import * as Sentry from "@sentry/react";
import {APIClient} from "../utils/APIClient";
import {API_URL} from "../settings/local_settings";

/**
 * Métricas que la plataforma no expone en su API, servidas por el backend.
 *
 * El catálogo de métricas del frontend (`metrics.js`) es una copia del del backend, y por eso
 * `Meta_IG_new_followers` desapareció del selector de columnas —que se arma en el backend— pero
 * siguió cuatro meses en el de objetivos, dejando configurar objetivos que el Tablero pintaba en
 * -100% para siempre. La lista la decide el backend y acá sólo se consume: es la única forma de
 * que las dos puntas no vuelvan a tener cada una su propia idea de qué se puede elegir.
 *
 * Viene con los ratios derivados YA expandidos (el backend descompone las fórmulas, que el
 * frontend no conoce).
 */
const TTL_MS = 5 * 60 * 1000;

let cache = null;
let cacheTimestamp = 0;
let pendingPromise = null;

let failureReported = false;

function isCacheFresh() {
    return cache !== null && (Date.now() - cacheTimestamp) < TTL_MS;
}

/**
 * Un endpoint roto acá no se ve en pantalla: el selector degrada y vuelve a ofrecer la métrica
 * que no se puede medir, sin ningún síntoma. Sin este reporte, el bug que el frente cierra podría
 * volver sin que nadie se entere. Se reporta UNA vez por sesión para no inundar Sentry: lo que
 * importa es enterarse, no contar cuántas pantallas lo pidieron.
 */
function reportUnavailableMetricsFailure(response) {
    if (failureReported) return;
    failureReported = true;
    Sentry.captureException(
        new Error('No se pudo leer el catálogo de métricas no disponibles'),
        {extra: {status: response && response.status, error_code: response && response.error_code}}
    );
}

export function fetchUnavailableMetrics({forceRefresh = false} = {}) {
    if (!forceRefresh && isCacheFresh()) {
        return Promise.resolve(cache);
    }
    if (pendingPromise) return pendingPromise;

    pendingPromise = new APIClient(API_URL + 'agency/unavailable-metrics/').call('GET')
        .then((response) => {
            // La guarda va por la FORMA de la respuesta: la vista responde el payload pelado, sin
            // `OKResponse`, así que un `status !== 'OK'` mandaría a error un 200 legítimo.
            if (!response || !Array.isArray(response.unavailable_metrics)) {
                // NO se cachea el fallo. Guardarlo dejaría pegado durante 5 minutos un «no hay
                // métricas no medibles» que es mentira, y el bug que este frente cierra volvería
                // en cada pantalla que monte el selector. El propio `APIClient` se niega a
                // cachear errores por la misma razón, con un TTL 10 veces más corto.
                reportUnavailableMetricsFailure(response);
                return [];
            }
            cache = response.unavailable_metrics;
            cacheTimestamp = Date.now();
            return cache;
        })
        // Si falla, la lista queda vacía y el selector se comporta como antes: es preferible
        // ofrecer de más a romper la pantalla de configuración de una Línea.
        .catch(() => [])
        .finally(() => {
            pendingPromise = null;
        });
    return pendingPromise;
}

export function useUnavailableMetrics() {
    const [unavailableMetrics, setUnavailableMetrics] = useState(cache || []);

    useEffect(() => {
        let cancelled = false;
        fetchUnavailableMetrics().then((result) => {
            if (cancelled) return;
            setUnavailableMetrics(result);
        });
        return () => { cancelled = true; };
    }, []);

    return {unavailableMetrics};
}
